import { useTranslation } from 'react-i18next';

import type { PricingPolicyQuery } from '@/graphql/generated';
import { useRateTypeLookup } from '@/hooks/useRateTypeLookup';

type PricingPolicyDetailsData = NonNullable<PricingPolicyQuery['pricingPolicy']>;
type PricingPolicyRateData =
	| PricingPolicyDetailsData['rate']
	| PricingPolicyDetailsData['overstayRate'];
type PricingPolicyRateStepData = NonNullable<
	NonNullable<PricingPolicyRateData>['rateSteps'][number]
>;

interface PricingPolicyRateDetailsProps {
	rate?: PricingPolicyRateData | null;
}

const parseNumber = (value: unknown): number | null => {
	if (value == null) {
		return null;
	}

	const numericValue = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(numericValue) ? numericValue : null;
};

const formatCurrency = (
	value: string | number | null | undefined,
	notSetText: string
) => {
	if (value == null) {
		return notSetText;
	}

	const numericValue = typeof value === 'number' ? value : Number(value);

	if (Number.isNaN(numericValue)) {
		return notSetText;
	}

	return new Intl.NumberFormat(undefined, {
		style: 'currency',
		currency: 'EUR'
	}).format(numericValue);
};

const formatDuration = (value: number | null | undefined, notSetText: string) => {
	if (value == null) {
		return notSetText;
	}

	const hours = Math.floor(value / 60);
	const minutes = value % 60;

	if (hours > 0 && minutes > 0) {
		return `${hours}h ${minutes}m`;
	}

	if (hours > 0) {
		return `${hours}h`;
	}

	return `${minutes}m`;
};

export const PricingPolicyRateDetails = ({
	rate
}: PricingPolicyRateDetailsProps) => {
	const { t: tPricingPolicies } = useTranslation('pricingPolicies');
	const { t: tRates } = useTranslation('rates');
	const { getRateTypeTitle } = useRateTypeLookup();

	const notSetText = tPricingPolicies('details.notSet');

	if (!rate) {
		return notSetText;
	}

	if (rate.rateTypeEnum === 'FLAT_RATE') {
		return (
			<div className="flex items-center justify-between gap-3">
				<span className="text-sm font-semibold text-foreground">
					{getRateTypeTitle('FLAT_RATE')}
				</span>
				<span className="text-sm font-semibold text-foreground">
					{formatCurrency(rate.flatRateAmount, notSetText)}
				</span>
			</div>
		);
	}

	const sortedSteps = [...(rate.rateSteps ?? [])].sort((left, right) => {
		const leftSequence = parseNumber(left.stepSequence) ?? 0;
		const rightSequence = parseNumber(right.stepSequence) ?? 0;
		return leftSequence - rightSequence;
	});

	return (
		<div className="space-y-2">
			<div className="text-sm font-semibold text-foreground">
				{getRateTypeTitle('TIERED')}
			</div>
			{sortedSteps.length === 0 ? (
				<div className="text-sm font-semibold text-foreground">
					{tRates('details.noConfigurationAvailable')}
				</div>
			) : (
				<div className="text-sm">
					{sortedSteps.map((step: PricingPolicyRateStepData, index: number) => {
						const stepNumber = parseNumber(step.stepSequence) ?? index + 1;
						const intervalLabel = formatDuration(
							parseNumber(step.intervalMinutes),
							notSetText
						);
						const maxIntervals = parseNumber(step.maxIntervals);
						const maxIntervalsLabel =
							maxIntervals == null
								? tRates('details.unlimited')
								: `${tRates('details.forMaximumLabel')} ${maxIntervals} ${tRates('details.timesLabel')}`;

						return (
							<div
								key={step.rateStepId}
								className="py-2 font-semibold leading-6 text-foreground border-b-2 border-dotted border-border/60 last:border-b-0"
							>
								{`${tRates('details.stepPrefix')} ${stepNumber} ${tRates('details.chargeLabel')} ${formatCurrency(step.intervalCharge, notSetText)} ${tRates('details.perLabel')} ${intervalLabel} ${maxIntervalsLabel}`}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
